import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { ConversationThread, type ConversationMessage } from '@repo/ui/Conversation';

import GET_MESSAGE_REQUESTS_FOR_ACCOUNT_Q from '../gql/getMessageRequestsForAccount.gql';

const PAGE_SIZE = 10;

// TEMP: visual preview toggle for the conversation thread. Set to `false` (or
// remove this block) before shipping. When true, the Apollo fetches are
// bypassed and a hardcoded back-and-forth around `currentMessage` is shown.
const SIMULATE_CONVERSATION = true;

const buildSimulatedConversation = (
  current: ConversationMessage
): ConversationMessage[] => {
  const base = new Date(current.insertedAt).getTime();
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  const at = (offset: number) => new Date(base + offset).toISOString();

  return [
    {
      id: 'sim-01',
      type: 'SMS_REQUEST',
      insertedAt: at(-day - 4 * hr),
      direction: 'INBOUND',
      displayBody: 'Hey, are we still on for tomorrow?',
      status: 'DELIVERED',
    },
    {
      id: 'sim-02',
      type: 'SMS_REQUEST',
      insertedAt: at(-day - 3.5 * hr),
      direction: 'OUTBOUND',
      displayBody: 'Yes! 3pm works for me.',
      status: 'DELIVERED',
    },
    {
      id: 'sim-03',
      type: 'SMS_REQUEST',
      insertedAt: at(-day - 3 * hr),
      direction: 'INBOUND',
      displayBody: 'Perfect. Same place as last time?',
      status: 'DELIVERED',
    },
    {
      id: 'sim-04',
      type: 'SMS_REQUEST',
      insertedAt: at(-day - 2 * hr),
      direction: 'OUTBOUND',
      displayBody:
        'Yep. I will be there a few minutes early to grab a table by the window.',
      status: 'DELIVERED',
    },
    {
      id: 'sim-05',
      type: 'MMS_REQUEST',
      insertedAt: at(-6 * hr),
      direction: 'INBOUND',
      displayBody: '📸 Look at this view from the window seat!',
      status: 'DELIVERED',
    },
    {
      id: 'sim-06',
      type: 'SMS_REQUEST',
      insertedAt: at(-5 * hr),
      direction: 'OUTBOUND',
      displayBody: 'Nice! On my way now, should be there in 10.',
      status: 'DELIVERED',
    },
    {
      id: 'sim-07',
      type: 'SMS_REQUEST',
      insertedAt: at(-4 * hr),
      direction: 'INBOUND',
      displayBody: 'ok sounds good',
      status: 'DELIVERED',
    },
    current,
    {
      id: 'sim-08',
      type: 'SMS_REQUEST',
      insertedAt: at(5 * min),
      direction: 'INBOUND',
      displayBody: 'Great, see you in a few',
      status: 'DELIVERED',
    },
    {
      id: 'sim-09',
      type: 'SMS_REQUEST',
      insertedAt: at(30 * min),
      direction: 'OUTBOUND',
      displayBody: 'That was a great catch-up. Thanks for making the time!',
      status: 'DELIVERED',
    },
    {
      id: 'sim-10',
      type: 'SMS_REQUEST',
      insertedAt: at(35 * min),
      direction: 'INBOUND',
      displayBody: 'Likewise! Let me know when you want to do it again.',
      status: 'DELIVERED',
    },
    {
      id: 'sim-11',
      type: 'SMS_REQUEST',
      insertedAt: at(2 * hr),
      direction: 'OUTBOUND',
      displayBody: 'How about next week Wednesday? Same time?',
      status: 'DELIVERED',
    },
    {
      id: 'sim-12',
      type: 'SMS_REQUEST',
      insertedAt: at(2.5 * hr),
      direction: 'INBOUND',
      displayBody: 'Works for me! I will put it on the calendar.',
      status: 'DELIVERED',
    },
    {
      id: 'sim-13',
      type: 'SMS_REQUEST',
      insertedAt: at(day - 4 * hr),
      direction: 'INBOUND',
      displayBody:
        'By the way, I wanted to mention something I forgot to bring up yesterday — would you be interested in joining the new project I am working on? I think your skills would be a perfect fit and there is a lot of interesting work to do.',
      status: 'DELIVERED',
    },
    {
      id: 'sim-14',
      type: 'SMS_REQUEST',
      insertedAt: at(day - 2 * hr),
      direction: 'OUTBOUND',
      displayBody: 'That sounds really interesting! Tell me more.',
      status: 'DELIVERED',
    },
    {
      id: 'sim-15',
      type: 'SMS_REQUEST',
      insertedAt: at(day - 1 * hr),
      direction: 'INBOUND',
      displayBody:
        'Awesome. I will send you the project brief and we can chat next week.',
      status: 'UNDELIVERED',
    },
  ];
};

export interface MessagesConversationsTabProps {
  accountId: string;
  hostNumber: string | null | undefined;
  remoteNumber: string | null | undefined;
  currentMessage?: ConversationMessage;
}

type Direction = 'older' | 'newer';

const mergeMessages = (
  existing: ConversationMessage[],
  incoming: ConversationMessage[]
): ConversationMessage[] => {
  // Dedupe by id across BOTH existing and incoming. Two reasons: (1) the
  // backend's range filter can be inclusive at the boundary, so a fetched
  // batch may contain the anchor message that we also add explicitly;
  // (2) overlapping older/newer fetches at the same timestamp can return
  // the same row twice.
  const seen = new Map<string, ConversationMessage>();
  for (const m of [...existing, ...incoming]) {
    if (!seen.has(m.id)) seen.set(m.id, m);
  }
  return [...seen.values()].sort(
    (a, b) => new Date(a.insertedAt).getTime() - new Date(b.insertedAt).getTime()
  );
};

const MessagesConversationsTab = ({
  accountId,
  hostNumber,
  remoteNumber,
  currentMessage,
}: MessagesConversationsTabProps) => {
  const apollo = useApolloClient();
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [hasMoreNewer, setHasMoreNewer] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loadingNewer, setLoadingNewer] = useState(false);
  const [error, setError] = useState(false);
  const initialLoadStarted = useRef(false);

  const simulatedMessages = useMemo(
    () => (SIMULATE_CONVERSATION && currentMessage ? buildSimulatedConversation(currentMessage) : null),
    [currentMessage]
  );

  const fetchBatch = useCallback(
    async (direction: Direction, anchor: string | Date): Promise<ConversationMessage[]> => {
      const range =
        direction === 'older'
          ? { start: { is_infinity: true }, end: { value: anchor } }
          : { start: { value: anchor }, end: { is_infinity: true } };

      const queryList = [
        { key: 'host_number', value: { value: hostNumber }, op: undefined },
        { key: 'remote_number', value: { value: remoteNumber }, op: undefined },
        { key: 'inserted_at', value: { range }, op: undefined },
      ];

      const result = await apollo.query({
        query: GET_MESSAGE_REQUESTS_FOR_ACCOUNT_Q,
        variables: { first: PAGE_SIZE, queryList, id: accountId },
        fetchPolicy: 'network-only',
      });

      return (
        result.data?.messageRequestsForAccount?.edges?.map((edge) => ({
          id: edge.node.id,
          type: edge.node.type,
          insertedAt: edge.node.insertedAt,
          direction: edge.node.direction,
          displayBody: edge.node.displayBody,
          status: edge.node.status,
        })) ?? []
      );
    },
    [apollo, accountId, hostNumber, remoteNumber]
  );

  useEffect(() => {
    if (SIMULATE_CONVERSATION) return;
    if (!accountId || !hostNumber || !remoteNumber || !currentMessage) return;
    if (initialLoadStarted.current) return;
    initialLoadStarted.current = true;

    setLoadingOlder(true);
    setLoadingNewer(true);

    Promise.all([
      fetchBatch('older', currentMessage.insertedAt),
      fetchBatch('newer', currentMessage.insertedAt),
    ])
      .then(([older, newer]) => {
        setMessages(mergeMessages([], [...older, currentMessage, ...newer]));
        if (older.length < PAGE_SIZE) setHasMoreOlder(false);
        if (newer.length < PAGE_SIZE) setHasMoreNewer(false);
      })
      .catch(() => setError(true))
      .finally(() => {
        setLoadingOlder(false);
        setLoadingNewer(false);
      });
  }, [accountId, hostNumber, remoteNumber, currentMessage, fetchBatch]);

  const loadOlder = useCallback(async () => {
    if (SIMULATE_CONVERSATION) return;
    if (loadingOlder || !hasMoreOlder || messages.length === 0) return;
    setLoadingOlder(true);
    try {
      const oldest = messages[0];
      const batch = await fetchBatch('older', oldest.insertedAt);
      if (batch.length < PAGE_SIZE) setHasMoreOlder(false);
      if (batch.length > 0) setMessages((prev) => mergeMessages(prev, batch));
    } catch {
      setError(true);
    } finally {
      setLoadingOlder(false);
    }
  }, [loadingOlder, hasMoreOlder, messages, fetchBatch]);

  const loadNewer = useCallback(async () => {
    if (SIMULATE_CONVERSATION) return;
    if (loadingNewer || !hasMoreNewer || messages.length === 0) return;
    setLoadingNewer(true);
    try {
      const newest = messages[messages.length - 1];
      const batch = await fetchBatch('newer', newest.insertedAt);
      if (batch.length < PAGE_SIZE) setHasMoreNewer(false);
      if (batch.length > 0) setMessages((prev) => mergeMessages(prev, batch));
    } catch {
      setError(true);
    } finally {
      setLoadingNewer(false);
    }
  }, [loadingNewer, hasMoreNewer, messages, fetchBatch]);

  if (!hostNumber || !remoteNumber) {
    return (
      <div className="flex justify-center items-center py-12 rounded-2xl border border-grey-400 dark:border-grey-700 bg-grey-100 dark:bg-blue-1000">
        <p className="text-grey-600 dark:text-grey-400">
          Host and remote numbers are required to load the conversation.
        </p>
      </div>
    );
  }

  return (
    <ConversationThread
      messages={simulatedMessages ?? messages}
      loading={
        !simulatedMessages && messages.length === 0 && (loadingOlder || loadingNewer)
      }
      error={error}
      hostNumber={hostNumber}
      remoteNumber={remoteNumber}
      highlightedMessageId={currentMessage?.id}
      hasMoreOlder={simulatedMessages ? false : hasMoreOlder}
      hasMoreNewer={simulatedMessages ? false : hasMoreNewer}
      loadingOlder={simulatedMessages ? false : loadingOlder}
      loadingNewer={simulatedMessages ? false : loadingNewer}
      onLoadOlder={loadOlder}
      onLoadNewer={loadNewer}
      getMessageHref={(m) =>
        `/a/${accountId}/messaging/${m.type === 'MMS_REQUEST' ? 'mmses' : 'smses'}/${m.id}`
      }
    />
  );
};

export default MessagesConversationsTab;
